import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store';
import { useEffect, useState } from 'react';
import './contents.css';
import { ContentPlayer } from '../../../app/components/audioPlayer/WaveSurfer/contentPlayer/ContentPlayer';
import { getInstruments, getUserContents } from '../../../app/features/sessions/sessionAudioContent/sliceUserContents';
import MyContentsUploadForm from '../../../app/components/forms/contents/MyContentsUploadForm';
import { useNavigate, useParams } from 'react-router-dom';
import { Content } from '../../../app/#interfaces/interfaces';
import {
    Box,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentShowEdit from '../../../app/components/modal/contentModal/ContentShowEdit';
import { handlePrivateRoutes } from '../../../app/api/requestRequirements';

const BG_MAIN        = '#15151a';
const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const LOG = '[Home]';

export default function Contents() {
    const loading = useSelector((state: RootState) => state.sessionUserContents.loading);
    const userValidated = useSelector((state: RootState) => state.session.loggedIn);
    const dispatch = useDispatch<AppDispatch>();
    const contents = useSelector((state: RootState) => state.sessionUserContents.userContents);
    const contentErrors = useSelector((state: RootState) => state.sessionUserContents.errors);
    const currentUser = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const contentsPosted = useSelector((state: RootState) => state.sessionUserContents.newMyContents);
    const noContentsCondition = (contents && contents.length === 0) && (contentsPosted && contentsPosted.length === 0);
    const { userNicknameRoute } = useParams();
    const MemorizedContentPlayer = useMemo(() => React.memo(ContentPlayer), []);
    const [contentsPage, setContentsPage] = useState(1);
    const loadingMoreContents = useSelector((state: RootState) => state.sessionUserContents.loadingMoreContents);
    const loadingCreateContent = useSelector((state: RootState) => state.sessionUserContents.loadingCreateContent);
    const [modalContentEditShowOpen, setModalContentEditShowOpen] = useState(false);
    const [selectedContentObj, setSelectedContentObj] = useState<Content>();
    const navigate = useNavigate();

    useEffect(() => {
        console.log(`${LOG} componente montado | utilizador: ${userNicknameRoute}`);
        return () => {
            console.log(`${LOG} componente desmontado`);
        };
    }, []);

    useEffect(() => {
        if (contents.length > 0) {
            console.log(`${LOG} conteúdos carregados | total: ${contents.length} | página actual: ${contentsPage}`, contents);
        }
    }, [contents]);

    useEffect(() => {
        if (contentErrors && contentErrors.length > 0) {
            console.error(`${LOG} erros ao carregar conteúdos`, contentErrors);
        }
    }, [contentErrors]);

    const fetchContents = async () => {
        if (currentUser && currentUser?.nickname !== userNicknameRoute) {
            console.log(`${LOG} utilizador autenticado (${currentUser.nickname}) difere da rota (${userNicknameRoute}) — a redirecionar`);
            const PRIVATE_ROUTES = handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser.nickname } });
            navigate(PRIVATE_ROUTES.USER);
        }

        if (authHeaders) {
            console.log(`${LOG} dispatch getUserContents | utilizador: ${userNicknameRoute} | página: ${contentsPage}`);
            await dispatch(getUserContents({
                authHeaders: authHeaders,
                routeParams: {
                    userNickname: userNicknameRoute,
                    contentsPage: contentsPage,
                },
            }));
        } else {
            console.warn(`${LOG} fetchContents ignorado — authHeaders ausentes`);
        }
    };

    useEffect(() => {
        console.log(`${LOG} useEffect[contentsPage] disparado | página: ${contentsPage}`);
        fetchContents();
    }, [contentsPage]);

    useEffect(() => {
        async function handleInstrumentsComboBox() {
            console.log(`${LOG} dispatch getInstruments`);
            await dispatch(getInstruments());
        }
        handleInstrumentsComboBox();
    }, []);

    useEffect(() => {
        console.log(`${LOG} useEffect[loadingMoreContents] disparado | loadingMoreContents: ${loadingMoreContents}`);

        const handleScroll = () => {
            const container = document.getElementById('container-ul-my-contents');
            if (!container) return;
            const { scrollTop, clientHeight, scrollHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight && !loadingMoreContents) {
                console.log(`${LOG} fim da lista atingido — a carregar página ${contentsPage + 1}`);
                setContentsPage(contentsPage + 1);
            }
        };

        setTimeout(() => {
            const container = document.getElementById('container-ul-my-contents');
            if (container) {
                container.addEventListener('scroll', handleScroll);
                return () => container.removeEventListener('scroll', handleScroll);
            }
        }, 100);
    }, [loadingMoreContents]);

    function handleOpenContentModal(contentObj: Content) {
        setSelectedContentObj(contentObj);
        setModalContentEditShowOpen(true);
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: 4 }}>
            {modalContentEditShowOpen === true ? (
                <ContentShowEdit
                    contentObj={selectedContentObj}
                    onCloseClick={() => setModalContentEditShowOpen(false)}
                />
            ) : null}

            <Container maxWidth='lg'>
                <Typography variant='h5' fontWeight={700} color={TEXT_PRIMARY} sx={{ mb: 0.5 }}>
                    {userNicknameRoute === currentUser?.nickname ? 'My Contents' : 'User Contents'}
                </Typography>
                <Box sx={{ width: 40, height: 3, bgcolor: BRAND_RED, borderRadius: 1, mb: 3 }} />

                {currentUser?.nickname === userNicknameRoute ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <MyContentsUploadForm />
                    </Box>
                ) : null}

                <Box sx={{ bgcolor: BG_CARD, borderRadius: '12px', border: BORDER_SUBTLE, overflow: 'hidden' }}>
                    <Box
                        id='container-ul-my-contents'
                        sx={{ maxHeight: '65vh', overflowY: 'auto' }}
                    >
                        {loading && contents.length === 0 ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <Box
                                    key={`skeleton-${i}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: BORDER_SUBTLE,
                                    }}
                                >
                                    <Skeleton
                                        variant='rectangular'
                                        animation='wave'
                                        width={45}
                                        height={78}
                                        sx={{ borderRadius: '5px', flexShrink: 0, bgcolor: 'rgba(255,255,255,0.08)' }}
                                    />
                                    <Skeleton
                                        variant='rectangular'
                                        animation='wave'
                                        height={78}
                                        sx={{ flex: 1, borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.08)' }}
                                    />
                                </Box>
                            ))
                        ) : noContentsCondition ? (
                            <Stack alignItems='center' justifyContent='center' sx={{ py: 8 }}>
                                <MusicNoteIcon sx={{ fontSize: 56, color: TEXT_SECONDARY, mb: 1 }} />
                                <Typography color={TEXT_SECONDARY}>No contents yet</Typography>
                            </Stack>
                        ) : (
                            contents.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{ borderBottom: BORDER_SUBTLE, px: 2, pt: 1, pb: 0.5 }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography
                                            variant='body2'
                                            color={TEXT_PRIMARY}
                                            noWrap
                                            sx={{ fontWeight: 500, flex: 1 }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <IconButton
                                            size='small'
                                            sx={{ color: TEXT_SECONDARY, ml: 1, flexShrink: 0 }}
                                            onClick={() => handleOpenContentModal(item)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 88, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                                            <Chip
                                                label={item.instrument?.name ?? 'other'}
                                                size='small'
                                                sx={{ bgcolor: BRAND_RED, color: '#fff', fontWeight: 600, maxWidth: 88 }}
                                            />
                                        </Box>
                                        <Box className='container-wave-surfer-player' sx={{ flex: 1, minWidth: 0 }}>
                                            <MemorizedContentPlayer fileUrl={`${item.fileUrl}`} />
                                        </Box>
                                    </Box>
                                </Box>
                            ))
                        )}

                        {loadingMoreContents && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress color='secondary' sx={{ color: BRAND_RED }} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
